import { useState, useEffect } from "react";
import Loader from "components/Loader";
import HelpCard from "components/Cards/HelpCard";
import KeywordIdeasTable from "components/Tables/KeywordIdeasTable";
import { submitGoogleAdsAnalysisRequest } from "services/buddyXApiHubService";
import MobileSupportTabBar from "components/TabBars/MobileSupportTabBar";
import { Button, Col, Form } from "react-bootstrap";
import Select from "react-select";
import Validator from "utils/Validator";
import classNames from "classnames";
import { css } from "aphrodite";
import pageStyles from "styles/pageStyles";
import { getInputFieldStyle, getButtonStyle, getSelectStyle } from "utils/formStyles";
import InsufficientCreditsPopup from "components/InsufficientCreditsPopup";
import ToastAlert from "components/Alert/ToastAlert";

export default function Page() {
  const [isFormValid, setIsFormValid] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [keyword, setKeyword] = useState("");
  const [country, setCountry] = useState({ value: "2036", label: "🇦🇺 AU" });
  const [formData, setFormData] = useState({
    country: "",
    keyword: "",
  });
  const [showSearchItem, setShowSearchItem] = useState("");
  const [data, setData] = useState();
  const [isResultGot, setIsResultGot] = useState(false);
  const [insufficientCreditsData, setInsufficientCreditsData] = useState(null);
  const [showInsufficientCredits, setShowInsufficientCredits] = useState(false);
  const [showErrorNotification, setShowErrorNotification] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [itemsPerPage, setItemsPerPage] = useState(25);

  const countryOptions = [
    { value: "2036", label: "🇦🇺 AU" },
    { value: "2840", label: "🇺🇸 USA" },
    { value: "2826", label: "🇬🇧 UK" },
    { value: "2392", label: "🇸🇬 SG" },
    { value: "2276", label: "🇳🇿 NZ" },
  ];

  const firstLevelTopic = "Content Editor";
  const secoundLevelTopic = "Keyword Discovery";
  const thirdLevelTopic = "Keyword";
  const [errors, setErrors] = useState({ keyword: "", country: "" });

  const handleInsufficientCredits = (balanceData) => {
    setInsufficientCreditsData(balanceData);
    setShowInsufficientCredits(true);
  };

  const closeInsufficientCredits = () => {
    setShowInsufficientCredits(false);
    setInsufficientCreditsData(null);
  };

  const handleKeywordChange = (e) => {
    setKeyword(e.target.value);
  };

  const handleCountryChange = (selectedOption) => {
    setCountry(selectedOption);
  };

  const handleSubmit = async () => {
    try {
      setIsLoading(true);
      const result = await submitGoogleAdsAnalysisRequest(
        formData,
        "keyword_ideas_single_keyword",
        handleInsufficientCredits
      );
      setShowSearchItem(formData.keyword);
      setData(result);
      setIsResultGot(true);
    } catch (error) {
      console.error("Submission error:", error);
    }
    setIsLoading(false);
  };

  const handleReset = () => {
    setData([]);
    setShowSearchItem("");
    setIsResultGot(false);
    setKeyword("");
    setCountry({ value: "2036", label: "🇦🇺 AU" });
    setErrors({ keyword: "", country: "" });
    setItemsPerPage(25);
  };

  const handleItemsPerPageChange = (selectedOption) => {
    setItemsPerPage(selectedOption.value);
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    let error = "";
    if (
      name === "keyword" &&
      !Validator.validateRequired(value, Validator.validateSingleKeyword)
    ) {
      error = "Please enter keyword";
    } else if (
      name === "country" &&
      !Validator.validateRequired(value, Validator.validateCountry)
    ) {
      error = "Select country";
    }
    setErrors((prevErrors) => ({
      ...prevErrors,
      [name]: error,
    }));
  };

  useEffect(() => {
    let isValid =
      Validator.validateRequired(keyword, Validator.validateSingleKeyword) &&
      Validator.validateRequired(country.value, Validator.validateCountry);
    if (isValid) {
      setFormData({
        country: [country.value],
        keyword: keyword,
      });
    }
    setIsFormValid(isValid);
  }, [country, keyword]);

  return (
    <div className={classNames("container-fluid", css(pageStyles.container))}>
      <h1 className=" d-flex fw-normal justify-content-start align-items-center fw-bold">
        {secoundLevelTopic}
      </h1>
      <p className="fw-normal justify-content-start align-items-center">
        {firstLevelTopic} / {secoundLevelTopic} / <b>{thirdLevelTopic}</b>
      </p>
      <p className="fw-normal justify-content-start align-items-center">
        Keyword research centered on a given keyword forms the cornerstone of
        SEO strategy by identifying search volume, competition, and intent
        alignment for that specific term, enabling targeted content creation
        that drives qualified traffic. In 2025's search ecosystem, dominated by
        AI-driven personalization and voice queries, this focused approach can
        yield 20-40% improvements in conversion rates by prioritizing
        high-intent variations, while revealing long-tail opportunities that
        reduce competition and enhance topical authority. Neglecting it risks
        misaligned efforts, resulting in stagnant rankings and inefficient
        resource allocation across digital campaigns.
      </p>
      <div className="row">
        <div className="col-lg-9 col-md-10 col-sm-10">
          <HelpCard
            topic={"How To Use"}
            description={"Begin with the given keyword as the seed in tools such as Google Keyword Planner, Ahrefs, or SEMrush to generate related terms, metrics (e.g., monthly searches, difficulty scores), and semantic clusters. Analyze intent (informational, navigational, transactional) to map opportunities, prioritizing those with balanced volume and low competition; expand to questions or modifiers via 'People Also Ask' features. Integrate findings into content planning by assigning keywords to pages or clusters, then track performance using Google Search Console for refinements, ensuring ongoing adaptation to evolving search trends."}
            image={"/icon/pin.svg"}
          />
        </div>
      </div>
      <div className=" p-2 mb-2 rounded">
        <div className=" d-flex fw-bold mb-0">Find Your Keywords</div>
        <div className="row mb-2 align-items-end">
          <div className="mb-2">
            <p className="fw-normal justify-content-start align-items-center">
              Enter a keyword or phrase to get relevant keyword ideas.
            </p>
            <Form.Group>
              <div className="d-flex mt-2">
                <Col sm={6} className="me-2">
                  <Form.Control
                    type="text"
                    name="keyword"
                    placeholder="Enter keyword"
                    value={keyword}
                    onChange={handleKeywordChange}
                    isInvalid={!!errors.keyword}
                    onBlur={handleBlur}
                    required
                    className="px-2"
                    style={getInputFieldStyle('type6')}
                  />
                </Col>
                <Select
                  name="country"
                  value={country}
                  onChange={handleCountryChange}
                  options={countryOptions}
                  className="me-2"
                  styles={{
                    container: (base) => ({
                      ...base,
                      width: "200px",
                    }),
                    control: (base) => ({
                      ...base,
                      ...getSelectStyle('type6'),
                    }),
                  }}
                />
                {errors.country && (
                  <div className="text-danger">{errors.country}</div>
                )}
                <div className="col-lg-4 col-md-4 col-sm-12 mb-2 me-1">
                  <div className="d-flex justify-content-start align-items-end">
                    <Button
                      variant="dark"
                      className="me-1"
                      onClick={handleSubmit}
                      disabled={!isFormValid || isLoading}
                      style={getButtonStyle('type6', 'primary')}
                    >
                      {isLoading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" />
                          Generating...
                        </>
                      ) : (
                        "Search"
                      )}
                    </Button>
                    <Button
                      variant="outline-dark"
                      className="me-1"
                      onClick={handleReset}
                      style={getButtonStyle('type6', 'outline')}
                    >
                      Reset
                    </Button>
                  </div>
                </div>
              </div>
            </Form.Group>
          </div>
        </div>
      </div>

      <div className="p-2">
        {showSearchItem !== "" && (
          <div className="d-flex justify-content-between align-items-center mb-3">
            <p className=" d-flex fw-bold mb-0">
              Keyword ideas for
              <span className="search-item">&nbsp;"{showSearchItem}"</span>
            </p>
            <div className="d-flex align-items-center">
              <span className="me-2">Show:</span>
              <Select
                value={{ value: itemsPerPage, label: itemsPerPage }}
                onChange={handleItemsPerPageChange}
                options={[
                  { value: 10, label: "10" },
                  { value: 25, label: "25" },
                  { value: 50, label: "50" },
                  { value: 75, label: "75" }
                ]}
                styles={{
                  container: (base) => ({
                    ...base,
                    width: "80px"
                  }),
                  control: (base) => ({
                    ...base,
                    ...getSelectStyle('type6'),
                    minHeight: "32px",
                    fontSize: "14px"
                  })
                }}
              />
            </div>
          </div>
        )}
        {isLoading && <Loader />}

        {isResultGot && <KeywordIdeasTable data={data} itemsPerPage={itemsPerPage} />}
      </div>
      {/* Insufficient Credits Popup */}
      <InsufficientCreditsPopup
        isOpen={showInsufficientCredits}
        onClose={closeInsufficientCredits}
        currentBalance={insufficientCreditsData?.currentBalance}
        requiredCredits={insufficientCreditsData?.requiredCredits}
        shortfall={insufficientCreditsData?.shortfall}
        actionName={insufficientCreditsData?.actionName}
      />
    </div>
  );
}
